import JSZip from "jszip";

const WRITE_BUFFER_SIZE = 8 * 1024 * 1024;

const RETRY_DELAYS = [5000, 15000, 30000];

export default async function zip(folder, files, callback) {
	const handle = await window.showSaveFilePicker({
		suggestedName: `${folder}.zip`,
		types: [{ description: "ZIP archive", accept: { "application/zip": [".zip"] } }],
	});

	const writable = await handle.createWritable();

	try {
		const z = new JSZip();

		for (const [index, [file, url]] of files.entries()) {
			const status = `${index + 1} / ${files.length}`;

			callback(status);

			const res = await fetchWithRetry(url, (attempt) => callback(`${status} (retry ${attempt})`));

			if (!res.ok) {
				throw new Error(`Failed to download ${file}: ${res.status} ${res.statusText}`);
			}

			// A Blob is backed by browser storage rather than the JS heap, unlike an ArrayBuffer
			z.folder(folder).file(`${file}.wav`, await res.blob(), { binary: true });
		}

		callback("Zipping...");

		await writeToDisk(z, writable);
	} catch (error) {
		await writable.abort();

		throw error;
	}
}

// After a long run of downloads Firebase Storage can answer with a redirect to a signed
// storage.googleapis.com URL. The browser sends `Origin: null` across that redirect, which the
// bucket's CORS config doesn't allow, so the fetch fails as a network error. Try again later.
async function fetchWithRetry(url, onRetry) {
	for (let attempt = 0; ; attempt++) {
		try {
			const res = await fetch(url);

			if ((res.status !== 429 && res.status < 500) || attempt >= RETRY_DELAYS.length) {
				return res;
			}
		} catch (error) {
			if (attempt >= RETRY_DELAYS.length) {
				throw error;
			}
		}

		onRetry(attempt + 1);

		await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
	}
}

function writeToDisk(z, writable) {
	return new Promise((resolve, reject) => {
		const stream = z.generateInternalStream({ type: "uint8array" });

		let pending = [];
		let pendingBytes = 0;

		// JSZip emits 16kb blocks, and every write to the file handle is a round trip to the
		// browser's file system backend. Batch them so the disk gets big writes instead.
		const flush = () => {
			const batch = new Uint8Array(pendingBytes);
			let offset = 0;

			for (const chunk of pending) {
				batch.set(chunk, offset);
				offset += chunk.length;
			}

			// The chunks are views into the file JSZip is reading, so drop them once copied
			pending = [];
			pendingBytes = 0;

			return writable.write(batch);
		};

		stream.on("data", (chunk) => {
			pending.push(chunk);
			pendingBytes += chunk.length;

			if (pendingBytes >= WRITE_BUFFER_SIZE) {
				// Only hold up JSZip while a full batch is on its way to disk
				stream.pause();

				flush().then(() => stream.resume(), reject);
			}
		});

		stream.on("error", reject);

		stream.on("end", () => {
			const remaining = pendingBytes > 0 ? flush() : Promise.resolve();

			remaining.then(() => writable.close()).then(resolve, reject);
		});

		stream.resume();
	});
}
